import {Directive, Input} from '@angular/core';
import {Router} from '@angular/router';
import {UntypedFormBuilder} from '@angular/forms';

import {SubmitForm} from './submitForm';
import {AlertService} from '../../service/alert.service';
import {AuthenticationService} from '../../service/authentication.service';
import {EnvironmentService} from '../../service/environment.service';

/**
 * Base class for the wizard step form components shared by the signup and eCommerce KYC
 * verification wizards: pre-fill hooks, a read-only mode, and a submit that allows guest admins.
 * The steps are exempt from the guest-admin block via {@link SubmitForm#allowsGuestUsers}: the
 * signup endpoints are public, and the KYC wizard is completed by guest admins.
 */
@Directive()
export abstract class WizardStepForm<T> extends SubmitForm
{
    /** Whether the step renders the submitted values with every field disabled. */
    @Input()
    readOnly = false;

    protected constructor(
        protected router: Router,
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService)
    {
        super(router, formBuilder, alertService, authenticationService, environmentService);
    }

    /**
     * Patches the prefilled model onto the form; a no-op until the form is built or when there is
     * no data.
     */
    patchPrefilledDataToForm(prefilledData: T): void
    {
        if (!this.form || !prefilledData)
        {
            return;
        }

        this.form.patchValue({
            ...prefilledData
        });
    }

    protected override allowsGuestUsers(): boolean
    {
        return true;
    }

    /**
     * Submits through {@link SubmitForm#onSubmit}, skipping validation of the disabled form in
     * read-only mode, where the step's submit only navigates.
     *
     * @param action optional action forwarded to the step's submit
     */
    override onSubmit(action = null): void
    {
        if (this.readOnly)
        {
            this.submit(action);
            return;
        }

        if (this.form.status !== 'VALID')
        {
            // the validation component only renders errors for touched controls
            this.form.markAllAsTouched();
        }
        super.onSubmit(action);
    }

    /**
     * Disables the whole form when the step is rendered in read-only "View submission" mode. Steps
     * call this after building their form in ngOnInit.
     */
    protected applyReadOnlyState(): void
    {
        if (this.readOnly)
        {
            this.form.disable();
        }
    }
}
